class CartShippingCalculator extends HTMLElement {
  static inFlightQuotes = new Map();

  constructor() {
    super();
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="zip"]');
    this.status = this.querySelector('.bevi-shipping-calculator__status');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.abortController = null;
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.form?.addEventListener('submit', this.onSubmit);
    this.input?.addEventListener('input', this.formatZip);

    // Cart fragments are replaced after Dawn Ajax updates. Recalculate a saved
    // CEP once the new component connects so rates always match the current cart.
    const savedZip = sessionStorage.getItem('bevi-shipping-zip');
    if (savedZip && this.input) {
      this.input.value = this.format(savedZip);
      this.calculate(savedZip);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        const zip = this.normalizedZip();
        if (zip) this.calculate(zip);
      });
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.input?.removeEventListener('input', this.formatZip);
    this.unsubscribe?.();
    this.abortController?.abort();
  }

  onSubmit = (event) => {
    event.preventDefault();
    const zip = this.normalizedZip();

    if (zip.length !== 8) {
      this.renderMessage('Informe um CEP válido com 8 números.', 'error');
      this.input?.focus();
      return;
    }

    sessionStorage.setItem('bevi-shipping-zip', zip);
    this.calculate(zip);
  };

  formatZip = () => {
    if (this.input) this.input.value = this.format(this.input.value);
  };

  format(value) {
    const zip = String(value).replace(/\D/g, '').slice(0, 8);
    return zip.length > 5 ? `${zip.slice(0, 5)}-${zip.slice(5)}` : zip;
  }

  normalizedZip() {
    return this.input?.value.replace(/\D/g, '') || '';
  }

  async calculate(zip) {
    if (zip.length !== 8) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    this.setLoading(true);
    this.renderMessage('Calculando opções de entrega…', 'loading');

    try {
      const rates = await CartShippingCalculator.getRates(zip, this.dataset.country || 'BR');
      if (signal.aborted) return;

      if (!rates.length) {
        this.renderMessage('Não encontramos opções de entrega para este CEP.', 'empty');
        return;
      }

      this.renderRates(rates);
    } catch (error) {
      if (signal.aborted) return;
      this.renderMessage(
        error?.code === 'timeout'
          ? 'O cálculo demorou mais que o esperado. Tente novamente.'
          : 'Não foi possível calcular o frete agora. Tente novamente.',
        'error'
      );
    } finally {
      if (!signal.aborted) this.setLoading(false);
    }
  }

  static getRates(zip, country) {
    const key = `${country}:${zip}`;
    if (!this.inFlightQuotes.has(key)) {
      const quote = this.fetchRates(zip, country).finally(() => this.inFlightQuotes.delete(key));
      this.inFlightQuotes.set(key, quote);
    }
    return this.inFlightQuotes.get(key);
  }

  static async fetchRates(zip, country) {
    const root = window.Shopify?.routes?.root || '/';
    const url = root.endsWith('/') ? root : `${root}/`;
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': country,
    });
    const prepareResponse = await fetch(`${url}cart/prepare_shipping_rates.json`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        shipping_address: {
          zip,
          country,
        },
      }),
    });

    if (!prepareResponse.ok && prepareResponse.status !== 202) throw new Error('Unable to prepare shipping rates');

    for (let attempt = 0; attempt < 12; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 750));
      const response = await fetch(`${url}cart/async_shipping_rates.json?${params}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to retrieve shipping rates');

      const data = await response.json();
      if (Array.isArray(data.shipping_rates)) return data.shipping_rates;
      if (data.errors) throw new Error('Shipping rates unavailable');
    }

    throw Object.assign(new Error('Shipping rate timeout'), { code: 'timeout' });
  }

  setLoading(isLoading) {
    if (!this.submitButton || !this.input) return;
    this.submitButton.disabled = isLoading;
    this.input.setAttribute('aria-busy', String(isLoading));
  }

  renderMessage(message, state) {
    if (!this.status) return;
    this.status.replaceChildren();
    const paragraph = document.createElement('p');
    paragraph.className = `bevi-shipping-calculator__message bevi-shipping-calculator__message--${state}`;
    paragraph.textContent = message;
    this.status.append(paragraph);
  }

  renderRates(rates) {
    if (!this.status) return;
    this.status.replaceChildren();
    const list = document.createElement('ul');
    list.className = 'bevi-shipping-calculator__rates list-unstyled';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
      style: 'currency',
      currency: this.dataset.currency || 'BRL',
    });

    rates.forEach((rate) => {
      const item = document.createElement('li');
      item.className = 'bevi-shipping-calculator__rate';
      const name = document.createElement('strong');
      name.textContent = rate.presentment_name || rate.name;
      const details = document.createElement('span');
      const price = Number(rate.price);
      const range = Array.isArray(rate.delivery_range) ? rate.delivery_range.filter(Boolean).join('–') : '';
      details.textContent = `${price === 0 ? 'Grátis' : money.format(price)}${range ? ` · ${range}` : ''}`;
      item.append(name, details);
      list.append(item);
    });
    this.status.append(list);
  }
}

if (!customElements.get('cart-shipping-calculator')) {
  customElements.define('cart-shipping-calculator', CartShippingCalculator);
}
